// components/FlowReport/ELFUnitDetailsCell.tsx
import React from "react";
import styles from "./FlowReport.module.css";
import { StatusBadge } from "./FlowReport";

interface ELFUnitDetail {
  Unit: string;
  SummaryStatus: string;
  StatusDescription?: string;
  UnitOutcome?: string;
}

interface ELFUnitDetailsCellProps {
  data: {
    eFileAcceptance_ELFStatus?: string;
    eFileAcceptance_FailedUnits?: string;
    eFileAcceptance_ELFUnitDetails?: ELFUnitDetail[];
  };
  onRowClick?: (row: any) => void;
}

/**
 * ELFUnitDetailsCell
 *
 * Renders eFile rejection details strictly as badges.
 *
 * Rules:
 * - If ELFStatus is Accepted -> show N/A badge
 * - If ELFStatus is Rejected -> show Rejected badge (clickable) with tooltip/details
 * - If no ELFStatus but all unit details are accepted -> show N/A badge
 * - If rejected details exist from unit details or legacy failed units -> show Rejected badge (clickable)
 * - All outputs are badges only
 */
const ELFUnitDetailsCell: React.FC<ELFUnitDetailsCellProps> = ({ data, onRowClick }) => {
  if (!data) {
    return <StatusBadge status="N/A" />;
  }

  const elfStatus = (data.eFileAcceptance_ELFStatus || "").trim().toLowerCase();
  const legacyFailedUnits = (data.eFileAcceptance_FailedUnits || "").trim();
  const elfUnitDetails = Array.isArray(data.eFileAcceptance_ELFUnitDetails)
    ? data.eFileAcceptance_ELFUnitDetails
    : [];

  /**
   * Builds tooltip content from ELF unit details.
   */
  const buildTooltipContent = (units: ELFUnitDetail[]): string => {
    return units
      .map((u) => {
        const unit = u.Unit || "Unknown Unit";
        const description = u.StatusDescription || u.SummaryStatus || "";
        const outcome = u.UnitOutcome ? ` (${u.UnitOutcome})` : "";
        return `${unit}: ${description}${outcome}`;
      })
      .join("\n");
  };

  /**
   * Returns all units for now.
   */
  const getRejectedUnits = (units: ELFUnitDetail[]): ELFUnitDetail[] => {
    return units.filter((u) => {
      const outcome = (u.UnitOutcome || "").toLowerCase();
      const status = (u.SummaryStatus || "").toLowerCase();
      return outcome || status;
    });
  };

  /**
   * Handles badge click safely.
   */
  const handleBadgeClick = (e: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>) => {
    e.stopPropagation();
    if (onRowClick) {
      onRowClick(data);
    }
  };

  // Rule 1: Explicit Accepted status => always N/A
  if (elfStatus === "accepted") {
    return <StatusBadge status="N/A" />;
  }

  // Rule 2: Explicit Rejected status => show clickable red badge
  if (elfStatus === "rejected") {
    if (elfUnitDetails.length > 0) {
      const rejectedUnits = getRejectedUnits(elfUnitDetails);

      if (rejectedUnits.length > 0) {
        const lastRejected = rejectedUnits[rejectedUnits.length - 1];
        const tooltipContent = `${lastRejected.Unit} - ${
          lastRejected.StatusDescription || lastRejected.SummaryStatus || "Rejected"
        }`;

        const displayText = `${lastRejected.Unit} - ${
          lastRejected.SummaryStatus || "Rejected"
        }`;

        return (
          <StatusBadge
            status="rejected"
            label={displayText}
            title={tooltipContent}
            onClick={(e) => {
              e.stopPropagation();
              onRowClick?.(data);
            }}
          />
        );
      }

      // Explicit rejected, but no rejected unit found in details.
      // Still show rejected badge with full tooltip.
      return (
        <StatusBadge
          status="Rejected"
          onClick={handleBadgeClick}
          title={buildTooltipContent(elfUnitDetails)}
        />
      );
    }

    if (legacyFailedUnits && legacyFailedUnits.toLowerCase() !== "none") {
      return (
        <StatusBadge
          status="Rejected"
          label={`${legacyFailedUnits} - REJECTED`}
          onClick={handleBadgeClick}
          title={legacyFailedUnits}
        />
      );
    }

    return (
      <StatusBadge
        status="Rejected"
        onClick={handleBadgeClick}
        title="Rejected"
      />
    );
  }

  // Rule 3: No explicit ELFStatus, but unit details exist
  if (elfUnitDetails.length > 0) {
    const allAccepted = elfUnitDetails.every((u) => {
      const outcome = (u.UnitOutcome || "").toLowerCase();
      const status = (u.SummaryStatus || "").toLowerCase();
      return outcome === "accepted" || status === "accepted";
    });

    if (allAccepted) {
      return <StatusBadge status="N/A" />;
    }

    const rejectedUnits = getRejectedUnits(elfUnitDetails);

    if (rejectedUnits.length > 0) {
      const lastRejected = rejectedUnits[rejectedUnits.length - 1];
      const tooltipContent = `${lastRejected.Unit} - ${
        lastRejected.StatusDescription || lastRejected.SummaryStatus || "Rejected"
      }`;

      const displayText = `${lastRejected.Unit} - ${
        lastRejected.SummaryStatus || "Rejected"
      }`;

      return (
        <StatusBadge
          status="rejected"
          label={displayText}
          title={tooltipContent}
          onClick={(e) => {
            e.stopPropagation();
            onRowClick?.(data);
          }}
        />
      );
    }
  }

  // Rule 4: Legacy fallback
  if (legacyFailedUnits && legacyFailedUnits.toLowerCase() !== "none") {
    return (
      <StatusBadge
        status="Rejected"
        onClick={handleBadgeClick}
        title={legacyFailedUnits}
      />
    );
  }

  // Default
  return <StatusBadge status="N/A" />;
};

export default ELFUnitDetailsCell;